import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  CaseSummary,
  ConversationDetail,
  ConversationSummary,
  Locale,
  Suggestion,
} from "../../../shared/types";
import type { DocumentSummary, Respondent } from "../lib/api";
import type { StageNotice } from "../App";
import { acquireBlob, type BlobRenderer, type BlobState } from "../blob/renderer";
import { MicError, MicRecorder } from "../audio/recorder";
import { SpeechPlayer } from "../audio/player";
import * as api from "../lib/api";
import { formatSince, useCopy } from "../lib/copy";
import { useTurn } from "../lib/useTurn";
import Transcript from "./Transcript";
import Dock from "./Dock";
import Notes from "./Notes";
import Suggestions from "./Suggestions";
import Cases from "./Cases";

interface StageProps {
  locale: Locale;
  conversation: ConversationDetail | null;
  /** Every conversation the consultant owns; the switcher filters to the open case. */
  conversations: ConversationSummary[];
  cases: CaseSummary[];
  /** The business being advised. Everything the dossier owns is read against it. */
  activeCaseId: number | null;
  theme: "dark" | "light";
  /** The name of the business being opened, while it is opening. Null at rest. */
  opening: string | null;
  /** Whatever the page has been told from outside a turn: a failure, or a fact. */
  notice: StageNotice | null;
  /**
   * Drops that notice. Pressing Return answers the complaint: whatever went wrong
   * with a rename or an upload is not what the founder is doing now, and the status
   * line belongs to the turn from here on.
   */
  onDismissNotice: () => void;
  /** Who the consultant thinks it is talking to on this case. Null before intake. */
  respondent: Respondent | null;
  documents: DocumentSummary[];
  /** A file is being read on the server. The blob thinks for exactly this long. */
  uploading: boolean;
  onIntakeComplete: (respondent: Respondent) => void;
  onRedoIntake: () => void;
  onUploadDocument: (file: File) => void;
  onDeleteDocument: (id: number) => void;
  onSwitchCase: (id: number) => void;
  onCreateCase: (name: string) => void;
  onRenameCase: (id: number, name: string) => void;
  onOpenConversation: (id: number) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: number) => void;
  /** Into the studies for this case. The conversation stays exactly where it is. */
  onOpenStudies: () => void;
  onToggleTheme: () => void;
  onChangeLocale: (locale: Locale) => void;
  onSignOut: () => void;
}

/** Mic state the turn machinery cannot know about: the device, not the conversation. */
type MicStatus = "idle" | "recording" | "transcribing" | "denied";
type Tone = "quiet" | "warn" | "error";

const STATUS_ID = "stage-status";
const DOCK_INPUT_ID = "dock-input";

/** Shown once, the first time this browser is asked for the microphone. */
const MIC_ASKED_KEY = "custdev.micasked";

/**
 * How long a recording runs before the status line stops saying "Listening" and says
 * something kinder. Long enough that a normal answer never sees it.
 */
const LISTENING_LONG_MS = 7000;

/** How long "Back. You were saying." stays up after a turn that had failed lands. */
const RECOVERED_MS = 4000;

/** How long the one sentence acknowledging a finished intake holds the status line. */
const INTAKE_DONE_MS = 6000;

/**
 * A status line long enough to be a sentence is set as one. 11px uppercase mono is a
 * label treatment, and a 67-character apology rendered as a label is unreadable — it
 * was the only channel a failed turn had.
 */
const LABEL_MAX_CHARS = 22;

/**
 * How much of the window the software keyboard has to take before the blob steps back.
 * Small viewport changes — a collapsing URL bar — must not trigger it.
 */
const KEYBOARD_MIN_PX = 120;

/**
 * The blob's docked size and the centre of the slot it docks into: a 44px dot in the
 * 56px top bar. Both are stated in docs/DESIGN.md 6.4 and both are read by the scroll
 * handler below rather than by CSS, because the distance it travels depends on where
 * the hero sits and that is a measured number.
 */
const DOCK_SIZE = 44;
const DOCK_CENTRE_Y = 28;

/** A conversation older than this reads as one you are coming back to, not continuing. */
const RESUMED_AFTER_MS = 20 * 60 * 1000;

export default function Stage({
  locale,
  conversation,
  conversations,
  cases,
  activeCaseId,
  theme,
  opening,
  notice: outerNotice,
  onDismissNotice,
  respondent,
  documents,
  uploading,
  onIntakeComplete,
  onRedoIntake,
  onUploadDocument,
  onDeleteDocument,
  onSwitchCase,
  onCreateCase,
  onRenameCase,
  onOpenConversation,
  onNewConversation,
  onDeleteConversation,
  onOpenStudies,
  onToggleTheme,
  onChangeLocale,
  onSignOut,
}: StageProps) {
  const copy = useCopy(locale);
  const conversationId = conversation ? conversation.id : null;
  const language = conversation ? conversation.language : locale;

  const [draft, setDraft] = useState("");
  const [micStatus, setMicStatus] = useState<MicStatus>("idle");
  const [notice, setNotice] = useState<{ text: string; tone: Tone } | null>(null);
  /**
   * Whether this conversation is still the consultant getting acquainted. Seeded
   * from the conversation and flipped by the `intake_complete` event; the stage is
   * remounted per conversation, so the seed is always this conversation's own.
   */
  const [kind, setKind] = useState<"intake" | "session">(conversation?.kind === "intake" ? "intake" : "session");
  /**
   * True for the whole life of a conversation that OPENED as an intake, even after
   * it flips: the label's slot keeps its height so the question under it does not
   * jump the moment the profile completes.
   */
  const startedAsIntake = useRef(conversation?.kind === "intake").current;
  const [commit, setCommit] = useState(false);
  const [listeningLong, setListeningLong] = useState(false);
  /**
   * What the founder could say next. Owned here rather than inside the turn, because
   * it has two sources — the `suggestions` event after a reply, and a fetch on
   * arrival or reload — and one writer of a piece of state is worth the prop.
   */
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  /**
   * The blob's BOX, not the blob: the canvas inside it is shared with every other
   * mount (see acquireBlob), so the element the layout and the dock animation are
   * written against has to be one this component owns.
   */
  const blobRef = useRef<HTMLDivElement | null>(null);
  /** One hidden file input, shared by the case menu's attach and the rail's. */
  const fileRef = useRef<HTMLInputElement | null>(null);
  const stageRef = useRef<HTMLElement | null>(null);
  const slotRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<BlobRenderer | null>(null);
  const recorderRef = useRef<MicRecorder | null>(null);
  const playerRef = useRef<SpeechPlayer | null>(null);
  const recordingRef = useRef(false);
  const endSpeechRef = useRef<(() => void) | null>(null);
  const findingsSeen = useRef(0);
  /**
   * The sentence that was sent and has not been answered. It goes back into the dock
   * if the turn fails: `errorGeneric` promises "I will keep what you already said",
   * and before this it was cleared on send and gone for good.
   */
  const pendingRef = useRef("");
  const failedRef = useRef(false);

  // Neither device is constructed until it is used: building a recorder at mount would
  // put a microphone in the page before there is anything to say into it.
  const getRecorder = useCallback((): MicRecorder => {
    recorderRef.current ??= new MicRecorder();
    return recorderRef.current;
  }, []);
  const getPlayer = useCallback((): SpeechPlayer => {
    playerRef.current ??= new SpeechPlayer();
    return playerRef.current;
  }, []);

  /**
   * The hook holds `speaking` for as long as this promise is pending, but the player
   * resolves `play` when audio *starts* and only fires `onended` when it finishes.
   * Barge-in stops playback without an `ended` event, so the resolver is kept here and
   * called by whoever cut the reply off.
   */
  const speak = useCallback(
    (url: string): Promise<void> => {
      const player = getPlayer();
      return new Promise<void>((resolve, reject) => {
        endSpeechRef.current = resolve;
        const clear = (): void => {
          player.onended = null;
          player.onerror = null;
          endSpeechRef.current = null;
        };
        player.onended = () => {
          clear();
          resolve();
        };
        // Rejecting is what lets the turn try the buffered POST path when the
        // streaming GET src fails. Barge-in never lands here: stop() resolves
        // through endSpeechRef without firing the player's error path.
        player.onerror = () => {
          clear();
          reject(new Error("speech playback failed"));
        };
        void player.play(url);
      });
    },
    [getPlayer],
  );

  const stopSpeech = useCallback((): void => {
    playerRef.current?.stop();
    const end = endSpeechRef.current;
    endSpeechRef.current = null;
    end?.();
  }, []);

  const turn = useTurn({
    conversationId,
    language,
    initialMessages: conversation ? conversation.messages : [],
    onSpeech: speak,
    onVoiceUnavailable: () => setNotice({ text: copy.voiceUnavailable, tone: "warn" }),
    onSuggestions: setSuggestions,
    onIntakeComplete: (known) => {
      // The label goes, the case learns who it is talking to, and one sentence in
      // the status line says so. Nothing navigates; the conversation just goes on.
      setKind("session");
      onIntakeComplete(known);
      setNotice({ text: copy.intakeDone, tone: "quiet" });
      window.setTimeout(() => {
        setNotice((current) =>
          current !== null && current.text === copy.intakeDone ? null : current,
        );
      }, INTAKE_DONE_MS);
    },
  });

  const stateRef = useRef(turn.state);
  stateRef.current = turn.state;
  const messagesRef = useRef(turn.messages);
  messagesRef.current = turn.messages;

  const micAvailable = MicRecorder.supported;

  useEffect(() => {
    recordingRef.current = micStatus === "recording";
  }, [micStatus]);

  // The blob is borrowed, not built: the stage is remounted per conversation and a
  // new WebGL2 context per conversation is a shader compile per conversation.
  useEffect(() => {
    const host = blobRef.current;
    if (!host) return;
    const { canvas, renderer } = acquireBlob();
    host.appendChild(canvas);
    rendererRef.current = renderer;
    renderer.start();
    return () => {
      rendererRef.current = null;
      // Parked, not destroyed. The context, the compiled program and the VAO are
      // the expensive part and the next conversation wants exactly those.
      renderer.stop();
      renderer.setLevel(0);
      canvas.remove();
    };
  }, []);

  // Transcription is a network wait like any other, and so are a case that has not
  // arrived and a document being read on the server: all of them borrow the thinking
  // state rather than leaving the blob idle while the founder waits on something.
  const blobState: BlobState =
    (opening !== null || micStatus === "transcribing" || uploading) && turn.state === "idle"
      ? "thinking"
      : turn.state;

  useEffect(() => {
    rendererRef.current?.setState(blobState);
  }, [blobState]);

  // One rAF loop, alive only while something is actually making sound.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    if (blobState !== "listening" && blobState !== "speaking") {
      renderer.setLevel(0);
      return;
    }
    let frame = 0;
    const tick = (): void => {
      const source = blobState === "listening" ? recorderRef.current : playerRef.current;
      renderer.setLevel(source ? source.level : 0);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [blobState]);

  /**
   * The scroll-driven dock (docs/DESIGN.md 6.4), which was specified and never built.
   *
   * Without it the blob is pinned at 38dvh forever: it painted a 190px disc across the
   * live question at rest, and across the transcript at every scroll position past it.
   * The blob now rests where the stage's own head room puts it and rides the page up
   * until it parks as a 44px dot in the top bar's slot.
   *
   * Driven from scroll offset rather than a boolean, so it is reversible and
   * interruptible. Layout is never touched: the box keeps its size and the change is
   * `translate` and `scale`, which is the only positional change the product makes.
   * The travel is `hero - 28px` rather than a written-down 160px so that the size and
   * the position finish together — sticky brings the box up at exactly one pixel per
   * pixel of scroll, and any other range would land the dot short of the bar.
   */
  useEffect(() => {
    let travel = 0;
    let shift = 0;
    let docked = 1;

    const measure = (): void => {
      const blob = blobRef.current;
      const stage = stageRef.current;
      const slot = slotRef.current;
      if (!blob || !stage || !slot) return;
      // Short windows drop the blob out of the flow entirely; there is nothing to dock.
      if (window.getComputedStyle(blob).position !== "sticky") {
        travel = 0;
        return;
      }
      const size = blob.offsetWidth;
      const hero = Number.parseFloat(window.getComputedStyle(stage).paddingTop);
      travel = Number.isFinite(hero) ? Math.max(0, hero - DOCK_CENTRE_Y) : 0;
      docked = size > 0 ? DOCK_SIZE / size : 1;
      // The blob is centred in the stage's content box and the slot is a fixed box in
      // the bar, so neither rect depends on the transform being measured.
      const slotBox = slot.getBoundingClientRect();
      const stageBox = stage.getBoundingClientRect();
      shift = slotBox.left + slotBox.width / 2 - (stageBox.left + stageBox.width / 2);
    };

    const apply = (): void => {
      const blob = blobRef.current;
      if (!blob) return;
      const progress = travel > 0 ? Math.min(1, Math.max(0, window.scrollY / travel)) : 0;
      blob.style.setProperty("--dock-p", progress.toFixed(4));
      blob.style.setProperty("--dock-x", `${shift.toFixed(1)}px`);
      blob.style.setProperty("--dock-scale", docked.toFixed(4));
    };

    const remeasure = (): void => {
      measure();
      apply();
    };

    remeasure();
    window.addEventListener("scroll", apply, { passive: true });
    window.addEventListener("resize", remeasure);
    return () => {
      window.removeEventListener("scroll", apply);
      window.removeEventListener("resize", remeasure);
    };
  }, []);

  // Browsers start an AudioContext suspended, and a suspended context yields a dead
  // level signal and a reply that plays silently. The first gesture is the only
  // chance to wake it.
  useEffect(() => {
    const unlock = (): void => {
      void MicRecorder.unlock();
      void getPlayer().unlock();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [getPlayer]);

  // What the consultant already believed before this session started, for this business
  // and no other. Applied only while nothing is in flight, so a slow response cannot
  // interrupt a live turn. Keyed on the ids rather than the objects: a parent that
  // rebuilds the conversation on every render would otherwise refetch on every render.
  useEffect(() => {
    if (conversationId === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const dossier = await api.getDossier(activeCaseId, controller.signal);
        if (controller.signal.aborted) return;
        if (dossier.findings.length === 0 || stateRef.current !== "idle") return;
        turn.reset(messagesRef.current, dossier.findings);
      } catch {
        // The rail starts from whatever this session produces. Nothing is lost.
      }
    })();
    return () => controller.abort();
    // Deliberately not keyed on the case: switching one commits the new name to the
    // switcher before the new conversation has arrived, and refetching in that window
    // would hang one business's notes beside another's transcript. The parent remounts
    // the stage per conversation, so a settled switch always brings a fresh effect.
  }, [conversationId, turn.reset]);

  /**
   * The chips for a page that has just been opened. The turn stream carries them
   * after every reply, but arriving at a conversation and reloading in the middle of
   * one are the two moments there is no stream — and they are exactly the moments a
   * founder is most likely to be looking at an empty field with nothing to say.
   *
   * Applied only if nothing has happened while the request was out: a founder who
   * started talking has answered the question these were an answer to.
   */
  useEffect(() => {
    if (conversationId === null) return;
    const controller = new AbortController();
    const before = messagesRef.current.length;
    void (async () => {
      try {
        const next = await api.getSuggestions(conversationId, controller.signal);
        if (controller.signal.aborted) return;
        if (stateRef.current !== "idle" || messagesRef.current.length !== before) return;
        setSuggestions(next);
      } catch {
        // An offer that did not arrive. The page works exactly as it did before.
      }
    })();
    return () => controller.abort();
  }, [conversationId]);

  useEffect(() => {
    if (turn.findings.length <= findingsSeen.current) {
      findingsSeen.current = turn.findings.length;
      return;
    }
    findingsSeen.current = turn.findings.length;
    setCommit(true);
    const timer = window.setTimeout(() => setCommit(false), 460);
    return () => window.clearTimeout(timer);
  }, [turn.findings.length]);

  // The dock rides the visual viewport; the blob steps back instead of being resized,
  // which on mid-range Android costs the whole animation.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = (): void => {
      const covered = window.innerHeight - viewport.height > KEYBOARD_MIN_PX;
      document.body.classList.toggle("kb-open", covered);
    };
    update();
    viewport.addEventListener("resize", update);
    return () => {
      viewport.removeEventListener("resize", update);
      document.body.classList.remove("kb-open");
    };
  }, []);

  useEffect(
    () => () => {
      playerRef.current?.destroy();
      recorderRef.current?.cancel();
    },
    [],
  );

  /**
   * A turn that failed hands the founder's sentence back rather than swallowing it,
   * and the next turn that lands says so. Both are the same promise `errorGeneric`
   * makes; neither was kept.
   */
  useEffect(() => {
    if (turn.error === null) return;
    failedRef.current = true;
    const text = pendingRef.current;
    if (text.length === 0) return;
    setDraft((current) => {
      // Anything typed since the send is newer and wins; the sentence stays pending
      // so the retry button still has it.
      if (current.length > 0) return current;
      pendingRef.current = "";
      return text;
    });
    const field = document.getElementById(DOCK_INPUT_ID);
    if (field instanceof HTMLInputElement) {
      field.focus();
      field.select();
    }
  }, [turn.error]);

  const settled = turn.state === "idle" && turn.error === null && turn.streamingText.length === 0;
  const lastRole = turn.messages.length > 0 ? turn.messages[turn.messages.length - 1]?.role : undefined;

  useEffect(() => {
    if (!settled || lastRole !== "assistant" || !failedRef.current) return;
    failedRef.current = false;
    pendingRef.current = "";
    setNotice({ text: copy.errorRecovered, tone: "quiet" });
    const timer = window.setTimeout(() => setNotice(null), RECOVERED_MS);
    return () => window.clearTimeout(timer);
  }, [copy.errorRecovered, lastRole, settled]);

  // "Take your time" belongs to a long recording and to nothing else.
  useEffect(() => {
    if (micStatus !== "recording") {
      setListeningLong(false);
      return;
    }
    const timer = window.setTimeout(() => setListeningLong(true), LISTENING_LONG_MS);
    return () => window.clearTimeout(timer);
  }, [micStatus]);

  const send = useCallback(
    (text: string, inputMode: "text" | "voice"): void => {
      const value = text.trim();
      if (value.length === 0 || !conversation) return;
      stopSpeech();
      setNotice(null);
      // And the one the page was told from outside. A turn starting is the moment
      // every previous complaint stops being what is happening.
      onDismissNotice();
      // Whatever was on offer was an offer about the previous reply. The next set
      // arrives with the next `done`.
      setSuggestions([]);
      pendingRef.current = value;
      void turn.send(value, inputMode);
    },
    [conversation, onDismissNotice, stopSpeech, turn],
  );

  const sendDraft = useCallback((): void => {
    // The draft survives anything that stops the send from happening at all, so a
    // paragraph is never retyped because a conversation failed to open.
    if (draft.trim().length === 0 || !conversation) return;
    send(draft, "text");
    setDraft("");
  }, [conversation, draft, send]);

  /** The way out of a turn that did not land: the sentence is already back in the dock. */
  const retry = useCallback((): void => {
    turn.clearError();
    if (draft.trim().length > 0) {
      sendDraft();
      return;
    }
    const again = pendingRef.current;
    if (again.length > 0) send(again, "text");
  }, [draft, send, sendDraft, turn]);

  const pickSuggestion = useCallback(
    (suggestion: Suggestion): void => {
      send(suggestion.text, "text");
      // The chip that was just pressed no longer exists, and the focus went with it.
      // Left alone it lands on the body, so it is handed back to the input, and only
      // where a keyboard is what focus means.
      if (window.matchMedia("(pointer: fine)").matches) {
        document.getElementById(DOCK_INPUT_ID)?.focus();
      }
    },
    [send],
  );

  const startRecording = useCallback(async (): Promise<void> => {
    if (!conversation || micStatus === "recording" || micStatus === "transcribing") return;
    // Barge-in: it stops talking the moment you do.
    stopSpeech();
    turn.clearError();
    // The one string that answers "can I talk to this?", shown while the browser's own
    // permission prompt is up and never again.
    let asked = true;
    try {
      asked = window.localStorage.getItem(MIC_ASKED_KEY) === "1";
    } catch {
      // Storage is blocked. Asking once per session is better than never asking.
    }
    setNotice(asked ? null : { text: copy.micAsk, tone: "quiet" });
    try {
      await getRecorder().start();
      setMicStatus("recording");
      setNotice(null);
      turn.setListening(true);
      try {
        window.localStorage.setItem(MIC_ASKED_KEY, "1");
      } catch {
        // Shown once more next time. Not worth a second thought.
      }
    } catch (err) {
      const code = err instanceof MicError ? err.code : "unavailable";
      setMicStatus(code === "denied" ? "denied" : "idle");
      setNotice({ text: code === "denied" ? copy.micDenied : copy.micBusy, tone: "warn" });
    }
  }, [
    conversation,
    copy.micAsk,
    copy.micBusy,
    copy.micDenied,
    getRecorder,
    micStatus,
    stopSpeech,
    turn,
  ]);

  const stopRecording = useCallback(
    async (discard: boolean): Promise<void> => {
      const recorder = recorderRef.current;
      if (!recorder || micStatus !== "recording") return;
      turn.setListening(false);
      if (discard) {
        recorder.cancel();
        setMicStatus("idle");
        return;
      }
      setMicStatus("transcribing");
      const clip = await recorder.stop();
      // Null means it was too short or too quiet to be speech. A mis-tap is not an
      // error and does not deserve a line of text.
      if (!clip) {
        setMicStatus("idle");
        return;
      }
      try {
        // The conversation id lets the server bias the ASR with this case's own
        // vocabulary — company and product names survive transcription that way.
        const result = await api.transcribe(clip, conversationId ?? undefined);
        setMicStatus("idle");
        if (result.text.trim().length > 0) send(result.text, "voice");
      } catch (err) {
        setMicStatus("idle");
        // A 4xx is this product talking; a 5xx is whatever a proxy felt like saying.
        const spoken =
          err instanceof api.ApiError && err.status >= 400 && err.status < 500
            ? err.message
            : copy.errorGeneric;
        setNotice({ text: spoken, tone: "error" });
      }
    },
    [conversationId, copy.errorGeneric, micStatus, send, turn],
  );

  const toggleMic = useCallback((): void => {
    if (micStatus === "recording") void stopRecording(false);
    else void startRecording();
  }, [micStatus, startRecording, stopRecording]);

  /** Opens the file picker. Called from a click, so the browser allows it. */
  const attach = useCallback((): void => {
    fileRef.current?.click();
  }, []);

  const filePicked = useCallback(
    (input: HTMLInputElement): void => {
      const file = input.files?.[0];
      // Cleared so choosing the same file twice in a row still fires a change event.
      input.value = "";
      if (file !== undefined) onUploadDocument(file);
    },
    [onUploadDocument],
  );

  /**
   * Hold SPACE to talk — but only from the empty dock field.
   *
   * The old guard was the other way round: it took SPACE everywhere EXCEPT an editable
   * element, which meant the field it autofocuses into typed a space and the page a
   * reader had scrolled did not page down. A reader lost the scroll key to a
   * microphone; the person the hint was written for never got it at all. Claiming it
   * only where the dock is focused and empty makes SPACE TO TALK literally true — that
   * is the exact state the dock prints it in — and gives the key back to everyone else.
   */
  useEffect(() => {
    if (!micAvailable) return;
    const fromDock = (target: EventTarget | null): boolean =>
      target instanceof HTMLInputElement && target.id === DOCK_INPUT_ID && target.value.length === 0;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && recordingRef.current) {
        event.preventDefault();
        void stopRecording(true);
        return;
      }
      if (event.code !== "Space" || event.repeat) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (!fromDock(event.target)) return;
      event.preventDefault();
      void startRecording();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      // Released anywhere: the founder may have moved focus mid-hold, and leaving the
      // microphone open because of it is worse than acting on a key we did not claim.
      if (event.code !== "Space" || !recordingRef.current) return;
      event.preventDefault();
      void stopRecording(false);
    };
    // Losing the window mid-hold would otherwise leave the microphone open.
    const onBlur = (): void => {
      if (recordingRef.current) void stopRecording(true);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [micAvailable, startRecording, stopRecording]);

  const messages = turn.messages;
  const streaming = turn.streamingText.length > 0;

  /**
   * Which message is in the hero.
   *
   * The newest reply, and it stays there while the founder answers it. It used to be
   * "the last message, if it is the consultant's", which meant the 38px centre of the
   * page emptied the instant the founder pressed Return and stayed empty for the
   * several seconds the reply took — the question they were answering deleted while
   * they waited for the answer to it. It is demoted instead, and the first delta
   * replaces it.
   */
  const featuredAt = useMemo(() => {
    if (streaming || opening !== null) return -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === "assistant") return i;
    }
    return -1;
  }, [messages, opening, streaming]);

  const featured = featuredAt === -1 ? undefined : messages[featuredAt];
  const headline = streaming ? turn.streamingText : (featured?.content ?? "");
  const past = useMemo(
    () =>
      opening !== null
        ? []
        : featuredAt === -1
          ? messages
          : messages.filter((_, index) => index !== featuredAt),
    [featuredAt, messages, opening],
  );

  /** True while the founder is waiting on a reply to the question still in the hero. */
  const waiting = featuredAt !== -1 && featuredAt !== messages.length - 1;

  /**
   * The opening, which is not the same as an empty conversation: the server seeds the
   * consultant's first question the moment a conversation is created, so `cold` was
   * never true by the time anything was on screen and the one sentence that tells a
   * founder they may speak was never rendered.
   */
  const opened = messages.every((message) => message.role !== "user") && !streaming;
  const cold = messages.length === 0 && !streaming;

  /**
   * How long ago this was last touched, shown once on arrival at a conversation that
   * was already under way. The product's whole promise is that it remembers across
   * sessions, and it rendered no timestamp anywhere.
   */
  const resumedAt = useRef<string | null>(
    conversation !== null &&
      conversation.messages.some((message) => message.role === "user") &&
      Date.now() - Date.parse(conversation.updatedAt) > RESUMED_AFTER_MS
      ? conversation.updatedAt
      : null,
  );
  const resumed = resumedAt.current !== null && !opened && messages.length === (conversation?.messages.length ?? 0);

  const caseName = useMemo(() => {
    const found = cases.find((item) => item.id === activeCaseId);
    const name = (found?.name ?? "").trim();
    return name.length > 0 ? name : copy.caseUntitled;
  }, [activeCaseId, cases, copy.caseUntitled]);

  /**
   * The one place state is written, so the order it is written in is the whole
   * contract. A LIVE state can never be stale; an outer notice always can be, and
   * ranking it above them meant one failed rename pinned "The name did not save."
   * over LISTENING, THINKING and SPEAKING for the rest of the session — 133 samples
   * across a full turn and the line never changed once. That is the page reading as
   * frozen. So: what is happening now, then what just happened, then what happened
   * somewhere else. `uploading` sits below the turn because a file being read is
   * only live until the founder speaks over it.
   */
  const status: { text: string; tone: Tone; dot: boolean } = turn.error
    ? { text: turn.error, tone: "error", dot: false }
    : notice
      ? { text: notice.text, tone: notice.tone, dot: false }
      : opening !== null
        ? { text: copy.caseOpening(opening), tone: "quiet", dot: false }
        : // No dot while recording: the lit mic border is the same live affordance, and
          // only one accent mark is allowed on screen at a time.
          turn.state === "listening"
          ? { text: listeningLong ? copy.listeningLong : copy.listening, tone: "quiet", dot: false }
          : turn.state === "thinking" || micStatus === "transcribing"
            ? { text: copy.thinking, tone: "quiet", dot: false }
            : turn.state === "speaking"
              ? { text: copy.speaking, tone: "quiet", dot: true }
              : uploading
                ? { text: copy.documentReading, tone: "quiet", dot: false }
                : outerNotice
                  ? { text: outerNotice.text, tone: outerNotice.tone, dot: false }
                  : { text: "", tone: "quiet", dot: false };

  const statusClass = [
    "status",
    status.tone === "quiet" ? "" : `status--${status.tone}`,
    status.text.length > LABEL_MAX_CHARS ? "status--sentence" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const busy = turn.state === "thinking" || micStatus === "transcribing" || !conversation;

  // The chips are for a founder with nothing in the field and nothing to say. They go
  // the moment either stops being true: one character typed, or the microphone open.
  const offering =
    draft.length === 0 && micStatus !== "recording" && opening === null ? suggestions : [];

  return (
    <div className="field">
      {/* The page has to have a name, and the honest one is the business on the table.
          Visually it is already in the switcher, so this is for the ear only. */}
      <h1 className="sr-only">{caseName}</h1>

      {/* The one file input, off screen. Both attach affordances click it. */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.txt,.md,.csv"
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => filePicked(event.currentTarget)}
      />

      <header className="topbar">
        {/* The slot the blob docks into on scroll. Empty, and never announced. */}
        <div className="topbar__slot" aria-hidden="true" ref={slotRef} />
        {/* Which business is on the table. First in the bar and never hidden behind an
            icon: this is the one thing you can be wrong about without noticing. */}
        <Cases
          locale={locale}
          cases={cases}
          conversations={conversations}
          activeCaseId={activeCaseId}
          activeConversationId={conversationId}
          respondent={respondent}
          onSwitch={onSwitchCase}
          onCreate={onCreateCase}
          onRename={onRenameCase}
          onOpenConversation={onOpenConversation}
          onNewConversation={onNewConversation}
          onDeleteConversation={onDeleteConversation}
          onRedoIntake={onRedoIntake}
          onOpenStudies={onOpenStudies}
          onAttach={attach}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onChangeLocale={onChangeLocale}
          onSignOut={onSignOut}
        />
        {/* The spacer sits here rather than at the end so the case name gets the
            middle of the bar instead of the three text buttons taking the left of
            it: the name was rendering 154px of a 269px business. Below 768px these
            three are hidden and the same controls appear inside the case menu. */}
        <div className="topbar__spacer" />
        <div className="topbar__chrome">
          <button className="btn btn--text" type="button" onClick={onToggleTheme}>
            {theme === "dark" ? copy.themeToLight : copy.themeToDark}
          </button>
          <button
            className="btn btn--text"
            type="button"
            onClick={() => onChangeLocale(locale === "en" ? "ru" : "en")}
          >
            {OTHER_LANGUAGE[locale]}
          </button>
          <button className="btn btn--text" type="button" onClick={onSignOut}>
            {copy.signOutCta}
          </button>
        </div>
      </header>

      <main className="stage" ref={stageRef}>
        <div className="blob" aria-hidden="true" ref={blobRef} />

        {/* One quiet marker while the consultant is still getting acquainted. The
            slot outlives the label by design: it keeps its height after the intake
            completes so the question under it does not move mid-conversation. */}
        {startedAsIntake && (
          <p className="stage__phase label">{kind === "intake" ? copy.intakeLabel : " "}</p>
        )}

        {/* The live region for the whole conversation. `aria-busy` is what keeps it
            from being read out delta by delta: the reply is announced once, when the
            turn settles. Before this a screen reader heard "Thinking" and then
            silence, and was never told the consultant had answered. */}
        <p
          className={waiting ? "q--live q--waiting" : "q--live"}
          lang={language}
          aria-live="polite"
          aria-atomic="true"
          aria-busy={streaming || turn.state === "thinking" || undefined}
        >
          {headline.length > 0
            ? headline
            : opening === null && (cold || opened)
              ? copy.emptyHero
              : ""}
        </p>

        {/* The hint and the chips are the same sentence twice: both say "here is how you
            start". The chips say it with real openers, so they win, and the hint comes
            back the moment there are none. */}
        {opened && offering.length === 0 && opening === null && (
          <p className="q-hint">{copy.emptyHint}</p>
        )}
        {resumed && (
          <p className="q-hint">
            <span className="num q-hint__when">{formatSince(resumedAt.current ?? "", locale)}</span>
            {copy.sessionResumed}
          </p>
        )}

        {/* A failed turn used to dead-end: a blank hero, no way back, and the typed
            sentence gone. The sentence is in the dock again and this is the button. */}
        {turn.error !== null && (
          <div className="q-recover">
            <button className="btn btn--quiet btn--sm" type="button" onClick={retry}>
              {copy.retryCta}
            </button>
          </div>
        )}

        <Transcript locale={locale} language={language} turns={past} />

        {/* Above the status line and below the transcript, so a keyboard reaches them
            on the way to the dock. They are an offer, and they own no state of their
            own: tapping one sends it, and `send` clears the row. */}
        <Suggestions locale={locale} suggestions={offering} onPick={pickSuggestion} />

        {/* The one place state is written. There are no toasts in this product. */}
        <p id={STATUS_ID} className={statusClass} role="status" aria-live="polite">
          {status.dot && <span className="status__dot" aria-hidden="true" />}
          {status.text}
        </p>

        <Dock
          locale={locale}
          value={draft}
          busy={busy}
          recording={micStatus === "recording"}
          micAvailable={micAvailable}
          micDenied={micStatus === "denied"}
          commit={commit}
          statusId={STATUS_ID}
          onChange={setDraft}
          onSend={sendDraft}
          onMicToggle={toggleMic}
        />
      </main>

      <Notes
        locale={locale}
        findings={opening === null ? turn.findings : []}
        documents={opening === null ? documents : []}
        caseId={activeCaseId}
        onAttach={attach}
        onDeleteDocument={onDeleteDocument}
      />
    </div>
  );
}

/**
 * Endonyms, not translations: the label for the other language is that language's own
 * name in every locale, so it needs no entry in copy.ts.
 */
const OTHER_LANGUAGE: Record<Locale, string> = { en: "Русский", ru: "English" };
